import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';

import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

import { useKnowledgeBuilder } from './KnowledgeBuilderProvider';
import { MarkdownPreview } from './MarkdownPreview';

import {
  buildKnowledgeReviewReadiness,
  markChecklistRowNotApplicable,
} from './reviewReadiness';

import { useKnowledgeBuilderColors } from '../../theme/knowledgeBuilderColors';

const EXPANDED_SIDEBAR_WIDTH = 300;
const COLLAPSED_SIDEBAR_WIDTH = 48;

export const ReviewPanel = () => {
  const {
    dirtyPaths,
    documents,
    proposedChanges,
    reviewConfirmed,
    saveDocument,
    setReviewConfirmed,
    updateDocument,
    validateSession,
    validation,
  } = useKnowledgeBuilder();

  const colors = useKnowledgeBuilderColors();

  const [sidebarOpen, setSidebarOpen] = useState(true);

  const readiness = useMemo(
    () =>
      buildKnowledgeReviewReadiness({
        documents,
        dirtyPaths,
        proposedChanges,
        validation,
      }),
    [dirtyPaths, documents, proposedChanges, validation],
  );

  const checklistDocument = documents.find(document =>
    /(^|\/)completeness-checklist\.md$/i.test(document.path),
  );

  const markNotApplicable = useCallback(
    (issue: { area: string; requirement: string }) => {
      if (!checklistDocument) {
        return;
      }

      const nextContent = markChecklistRowNotApplicable(
        checklistDocument.content,
        issue,
      );

      updateDocument(checklistDocument.path, nextContent);
      saveDocument(checklistDocument.path, nextContent);
    },
    [
      checklistDocument,
      saveDocument,
      updateDocument,
    ],
  );

  const [selectedPath, setSelectedPath] = useState(
    documents.find(document =>
      /completeness-checklist\.md$/i.test(document.path),
    )?.path ?? documents[0]?.path,
  );

  const selectedDocument =
    documents.find(document => document.path === selectedPath) ??
    documents[0];

  useEffect(() => {
    if (!selectedDocument && documents[0]) {
      setSelectedPath(documents[0].path);
    }
  }, [documents, selectedDocument]);

  const toggleSidebar = () => {
    setSidebarOpen(current => !current);
  };

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <aside
        className="knowledge-builder-scrollbar"
        style={{
          width: sidebarOpen
            ? EXPANDED_SIDEBAR_WIDTH
            : COLLAPSED_SIDEBAR_WIDTH,

          flex: `0 0 ${
            sidebarOpen
              ? EXPANDED_SIDEBAR_WIDTH
              : COLLAPSED_SIDEBAR_WIDTH
          }px`,

          minWidth: 0,
          overflowX: 'hidden',
          overflowY: sidebarOpen ? 'auto' : 'hidden',

          boxSizing: 'border-box',

          borderRight: `1px solid ${colors.border.default}`,
          background: colors.bg.panel,

          transition:
            'width 180ms ease, flex-basis 180ms ease',
        }}
      >
        <div
          style={{
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: sidebarOpen
              ? 'space-between'
              : 'center',

            padding: sidebarOpen ? '0 8px 0 16px' : 0,

            boxSizing: 'border-box',
            borderBottom: `1px solid ${colors.border.default}`,
          }}
        >
          {sidebarOpen && (
            <Typography
              variant="subtitle2"
              style={{
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              Review
            </Typography>
          )}

          <Tooltip
            title={
              sidebarOpen
                ? 'Collapse review panel'
                : 'Expand review panel'
            }
          >
            <IconButton
              size="small"
              onClick={toggleSidebar}
              aria-label={
                sidebarOpen
                  ? 'Collapse review panel'
                  : 'Expand review panel'
              }
            >
              {sidebarOpen ? (
                <ChevronLeftIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </div>

        {sidebarOpen ? (
          <div
            style={{
              padding: 16,
              boxSizing: 'border-box',
            }}
          >
            <Typography variant="subtitle1">
              Review readiness
            </Typography>

            <Typography
              variant="body2"
              style={{
                color: colors.text.muted,
                marginTop: 2,
              }}
            >
              Inspect every generated file before confirming the
              Scaffolder review.
            </Typography>

            <div style={{ margin: '14px 0' }}>
              <Chip
                size="small"
                icon={
                  readiness.ready ? (
                    <CheckCircleIcon />
                  ) : (
                    <ErrorOutlineIcon />
                  )
                }
                label={
                  readiness.ready
                    ? 'Ready for confirmation'
                    : 'Needs attention'
                }
                style={{
                  background: readiness.ready
                    ? colors.bg.hover
                    : colors.status.warningBg,

                  color: readiness.ready
                    ? colors.accent.blue
                    : colors.status.warningText,
                }}
              />
            </div>

            {readiness.blockers
              .filter(
                blocker =>
                  !blocker.startsWith(
                    'Resolve or explicitly mark not applicable',
                  ),
              )
              .map(blocker => (
                <Typography
                  key={blocker}
                  variant="body2"
                  style={{
                    marginBottom: 8,
                    color: colors.status.warningText,
                  }}
                >
                  • {blocker}
                </Typography>
              ))}

            {readiness.checklistIssues.map(
              (issue, index) => (
                <div
                  key={`${issue.area}-${issue.requirement}-${index}`}
                  style={{
                    marginBottom: 8,
                    padding: 8,
                    borderRadius: 6,
                    background: colors.status.warningBg,
                  }}
                >
                  <Typography
                    variant="body2"
                    style={{ fontWeight: 600 }}
                  >
                    {issue.area} — {issue.requirement}
                  </Typography>

                  <Typography
                    variant="caption"
                    style={{
                      display: 'block',
                      marginTop: 2,
                      marginBottom: 6,
                      color: colors.status.warningText,
                    }}
                  >
                    {issue.status}

                    {issue.nextQuestion
                      ? ` · ${issue.nextQuestion}`
                      : ''}
                  </Typography>

                  <Button
                    type="button"
                    size="small"
                    variant="text"
                    disabled={!checklistDocument}
                    onClick={() =>
                      markNotApplicable(issue)
                    }
                  >
                    Mark not applicable
                  </Button>
                </div>
              ),
            )}

            <Button
              type="button"
              size="small"
              variant="outlined"
              startIcon={
                <AssignmentTurnedInIcon />
              }
              onClick={validateSession}
              style={{
                margin: '8px 0 14px',
              }}
            >
              Run validation
            </Button>

            <Typography variant="subtitle2">
              Files ({documents.length})
            </Typography>

            <div style={{ marginTop: 6 }}>
              {documents.map(document => {
                const selected =
                  document.path ===
                  selectedDocument?.path;

                return (
                  <button
                    key={document.path}
                    type="button"
                    onClick={() =>
                      setSelectedPath(document.path)
                    }
                    style={{
                      width: '100%',

                      border: selected
                        ? `1px solid ${colors.border.default}`
                        : '1px solid transparent',

                      borderRadius: 6,

                      padding: '8px 9px',
                      marginBottom: 3,

                      textAlign: 'left',
                      cursor: 'pointer',

                      color: colors.text.primary,

                      background: selected
                        ? colors.bg.hover
                        : 'transparent',
                    }}
                  >
                    <span
                      style={{
                        display: 'block',
                        fontSize: 12,
                        fontWeight: selected
                          ? 600
                          : 400,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={document.path}
                    >
                      {document.path}
                    </span>

                    <span
                      style={{
                        display: 'block',
                        marginTop: 2,
                        fontSize: 10,
                        color: colors.text.muted,
                      }}
                    >
                      revision {document.revision} ·{' '}
                      {document.status}
                    </span>
                  </button>
                );
              })}
            </div>

            <div
              style={{
                marginTop: 16,
                paddingTop: 12,
                borderTop: `1px solid ${colors.border.default}`,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={reviewConfirmed}
                    disabled={!readiness.ready}
                    onChange={event =>
                      setReviewConfirmed(
                        event.target.checked,
                      )
                    }
                  />
                }
                label="I reviewed the generated output"
              />

              {!readiness.ready && (
                <Typography
                  variant="caption"
                  style={{
                    color: colors.text.muted,
                  }}
                >
                  Resolve all blockers before confirming.
                </Typography>
              )}
            </div>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingTop: 12,
              gap: 8,
            }}
          >
            <Tooltip
              title={`${documents.length} generated files`}
            >
              <div
                style={{
                  position: 'relative',
                  width: 32,
                  height: 32,

                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <DescriptionOutlinedIcon
                  fontSize="small"
                  style={{
                    color: colors.text.muted,
                  }}
                />

                <span
                  style={{
                    position: 'absolute',
                    right: -2,
                    top: -3,

                    minWidth: 14,
                    height: 14,

                    padding: '0 3px',

                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',

                    boxSizing: 'border-box',

                    borderRadius: 8,

                    background: colors.accent.blue,
                    color: '#fff',

                    fontSize: 9,
                    lineHeight: '14px',
                  }}
                >
                  {documents.length}
                </span>
              </div>
            </Tooltip>

            <Tooltip
              title={
                readiness.ready
                  ? 'Ready for confirmation'
                  : 'Review requires attention'
              }
            >
              {readiness.ready ? (
                <CheckCircleIcon
                  fontSize="small"
                  style={{
                    color: colors.accent.blue,
                  }}
                />
              ) : (
                <ErrorOutlineIcon
                  fontSize="small"
                  style={{
                    color:
                      colors.status.warningText,
                  }}
                />
              )}
            </Tooltip>
          </div>
        )}
      </aside>

      <main
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflow: 'hidden',
          background: colors.bg.panel,
        }}
      >
        {selectedDocument ? (
          <MarkdownPreview
            document={selectedDocument}
          />
        ) : (
          <Typography style={{ padding: 24 }}>
            No document to review.
          </Typography>
        )}
      </main>
    </div>
  );
};